import React, { useState, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiClient } from '@/lib/api';
import { Wifi, Bluetooth, Radio, Search, MapPin, Download, BarChart3, Shield, Filter, X } from 'lucide-react';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface UnifiedDevice {
  device_type: 'wifi' | 'bluetooth' | 'cell';
  latitude: number;
  longitude: number;
  name?: string;
  netid: string;
  firsttime: string;
  lasttime: string;
  lastupdt: string;
  country: string;
  region?: string;
  city?: string;
  ssid?: string;
  encryption?: string;
  channel?: number;
  frequency?: number;
  qos?: number;
  mcc?: number;
  mnc?: number;
  lac?: number;
  cid?: number;
  averageSignalStrength?: number | null;
  range?: number | null;
  samples?: number | null;
  changeable?: boolean | null;
  radio?: string | null;
  rnc?: number | null;
  tac?: number | null;
  sid?: number | null;
  nid?: number | null;
  bid?: number | null;
  source?: string | null;
}

// Custom icons for different device types
const wifiIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 9L3 11C7.97 6.03 16.03 6.03 21 11L23 9C16.93 2.93 7.07 2.93 1 9ZM9 17L12 20L15 17C13.35 15.34 10.65 15.34 9 17ZM5 13L7 15C9.76 12.24 14.24 12.24 17 15L19 13C15.14 9.14 8.86 9.14 5 13Z" fill="#10b981"/>
    </svg>
  `),
  iconSize: [25, 25],
  iconAnchor: [12, 25],
  popupAnchor: [0, -25],
});

const bluetoothIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.71 7.71L12 2L10.59 3.41L14.17 7H6.5C5.67 7 5 7.67 5 8.5V15.5C5 16.33 5.67 17 6.5 17H11.5L13.5 19L12 20.5L8.75 17.25L6.5 17V15.5L10.59 11.41L12 12.82V20.5L17.71 14.79L13.41 10.5L17.71 7.71Z" fill="#3b82f6"/>
    </svg>
  `),
  iconSize: [25, 25],
  iconAnchor: [12, 25],
  popupAnchor: [0, -25],
});

const cellIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 1.01L7 1C5.9 1 5 1.9 5 3V21C5 22.1 5.9 23 7 23H17C18.1 23 19 22.1 19 21V3C19 1.9 18.1 1.01 17 1.01ZM17 19H7V5H17V19Z" fill="#8b5cf6"/>
    </svg>
  `),
  iconSize: [25, 25],
  iconAnchor: [12, 25],
  popupAnchor: [0, -25],
});

// Map updater component
function MapUpdater({ center, zoom }: { center: [number, number], zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
}

const WiFiIntelligence: React.FC = () => {
  const [devices, setDevices] = useState<UnifiedDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showLEATools, setShowLEATools] = useState(false);
  
  // Device type selection
  const [deviceTypes, setDeviceTypes] = useState({
    wifi: true,
    bluetooth: false,
    cell: false
  });
  
  // Search location
  const [searchLocation, setSearchLocation] = useState({
    type: 'city' as 'city' | 'coordinates' | 'quick',
    city: '',
    country: '',
    lat1: 12.9,
    lat2: 13.1,
    lon1: 77.5,
    lon2: 77.7,
    quickLocation: ''
  });
  
  // Advanced filters
  const [filters, setFilters] = useState({
    ssid: '',
    encryption: '',
    freeOnly: false,
    payOnly: false
  });
  
  // LEA Tools
  const [cellLookup, setCellLookup] = useState({
    mcc: '',
    mnc: '',
    lac: '',
    cellid: '',
    radio: 'GSM'
  });
  const [cellLookupResult, setCellLookupResult] = useState<any>(null);
  const [areaAnalysisResult, setAreaAnalysisResult] = useState<any>(null);

  // Quick locations
  const quickLocations = [
    { name: 'Bangalore, India', lat1: 12.9, lat2: 13.1, lon1: 77.5, lon2: 77.7, city: 'Bengaluru', country: 'IN' },
    { name: 'New York, USA', lat1: 40.7, lat2: 40.8, lon1: -74.0, lon2: -73.9, city: 'New York', country: 'US' },
    { name: 'London, UK', lat1: 51.4, lat2: 51.6, lon1: -0.2, lon2: 0.1, city: 'London', country: 'GB' },
    { name: 'Tokyo, Japan', lat1: 35.6, lat2: 35.8, lon1: 139.6, lon2: 139.8, city: 'Tokyo', country: 'JP' },
  ];

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: any = {
        latrange1: searchLocation.lat1,
        latrange2: searchLocation.lat2,
        longrange1: searchLocation.lon1,
        longrange2: searchLocation.lon2,
        include_wifi: deviceTypes.wifi,
        include_bluetooth: deviceTypes.bluetooth,
        include_cell: deviceTypes.cell,
        results_per_page: 500
      };
      
      if (filters.ssid) params.ssid = filters.ssid;
      if (filters.encryption) params.encryption = filters.encryption;
      if (filters.freeOnly) params.freenet = true;
      if (filters.payOnly) params.paynet = true;
      if (searchLocation.city) params.city = searchLocation.city;
      if (searchLocation.country) params.country = searchLocation.country;
      
      const response = await apiClient.searchAllDevices(params) as any;
      
      if (response?.devices && response.devices.length > 0) {
        setDevices(response.devices);
      } else {
        setDevices([]);
        // Provide a more helpful error message when external data sources return nothing
        const source = response?.source;
        if (source === 'wifimap_fallback') {
          setError(
            'No WiFi data returned. External providers (WiGLE / WiFiMap) are either rate-limited, blocked by your ISP/proxy, or returned no hotspots for this area. ' +
            'Try a different city (e.g. Bengaluru, Chennai) or configure valid WiGLE credentials / proxy on the backend.'
          );
        } else {
          setError('No devices found for the selected area. Try expanding the area or removing filters.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch devices (check backend logs and external WiFi providers)');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [searchLocation, deviceTypes, filters]);

  const handleQuickLocation = (loc: typeof quickLocations[0]) => {
    setSearchLocation({
      ...searchLocation,
      type: 'quick',
      quickLocation: loc.name,
      lat1: loc.lat1,
      lat2: loc.lat2,
      lon1: loc.lon1,
      lon2: loc.lon2,
      city: loc.city,
      country: loc.country
    });
  };

  const handleCellLookup = async () => {
    if (!cellLookup.mcc || !cellLookup.mnc || !cellLookup.lac || !cellLookup.cellid) {
      setError('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      const result = await apiClient.lookupCellTower({
        mcc: parseInt(cellLookup.mcc),
        mnc: parseInt(cellLookup.mnc),
        lac: parseInt(cellLookup.lac),
        cellid: parseInt(cellLookup.cellid),
        radio: cellLookup.radio
      });
      setCellLookupResult(result);
      // Update map to show location
      if (result) {
        setSearchLocation(prev => ({
          ...prev,
          lat1: (result as any).lat - 0.01,
          lat2: (result as any).lat + 0.01,
          lon1: (result as any).lon - 0.01,
          lon2: (result as any).lon + 0.01
        }));
      }
    } catch (err: any) {
      setError(err.message || 'Cell lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAreaAnalysis = async () => {
    setLoading(true);
    try {
      const result = await apiClient.countOpenCellIDCells({
        latmin: searchLocation.lat1,
        lonmin: searchLocation.lon1,
        latmax: searchLocation.lat2,
        lonmax: searchLocation.lon2
      });
      setAreaAnalysisResult(result);
    } catch (err: any) {
      setError(err.message || 'Area analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const cellTowers = devices.filter(d => d.device_type === 'cell');
    if (cellTowers.length === 0) {
      setError('No cell towers to export');
      return;
    }
    
    const csvHeaders = ['Type', 'Latitude', 'Longitude', 'MCC', 'MNC', 'LAC', 'CID', 'Radio', 'Signal', 'Country', 'City'];
    const csvRows = cellTowers.map(d => [
      d.device_type,
      d.latitude,
      d.longitude,
      d.mcc || '',
      d.mnc || '',
      d.lac || '',
      d.cid || '',
      d.radio || '',
      d.averageSignalStrength || '',
      d.country,
      d.city || ''
    ]);
    const csv = [csvHeaders, ...csvRows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cell-towers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getDeviceIcon = (device: UnifiedDevice) => {
    if (device.device_type === 'wifi') return wifiIcon;
    if (device.device_type === 'bluetooth') return bluetoothIcon;
    return cellIcon;
  };

  const deviceCounts = {
    wifi: devices.filter(d => d.device_type === 'wifi').length,
    bluetooth: devices.filter(d => d.device_type === 'bluetooth').length,
    cell: devices.filter(d => d.device_type === 'cell').length
  };

  const mapCenter: [number, number] = [
    (searchLocation.lat1 + searchLocation.lat2) / 2,
    (searchLocation.lon1 + searchLocation.lon2) / 2
  ];
  const mapZoom = 13;

  return (
    <div className="min-h-screen bg-background">
      {/* Header Section */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <Wifi className="w-8 h-8 text-primary" />
                WiFi Intelligence
              </h1>
              <p className="text-muted-foreground mt-2">
                Discover and analyze WiFi networks, Bluetooth devices, and cell towers worldwide
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex gap-2">
                <div className="px-4 py-2 bg-primary/10 rounded-lg border border-primary/20">
                  <div className="text-sm text-muted-foreground">Total Devices</div>
                  <div className="text-2xl font-bold text-primary">{devices.length}</div>
                </div>
                <div className="px-4 py-2 bg-green-500/10 rounded-lg border border-green-500/20">
                  <div className="text-sm text-muted-foreground">WiFi</div>
                  <div className="text-2xl font-bold text-green-500">{deviceCounts.wifi}</div>
                </div>
                <div className="px-4 py-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                  <div className="text-sm text-muted-foreground">Bluetooth</div>
                  <div className="text-2xl font-bold text-blue-500">{deviceCounts.bluetooth}</div>
                </div>
                <div className="px-4 py-2 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <div className="text-sm text-muted-foreground">Cell Towers</div>
                  <div className="text-2xl font-bold text-purple-500">{deviceCounts.cell}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Search & Controls */}
          <div className="lg:col-span-1 space-y-4">
            {/* Device Type Selection */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Device Types
              </h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={deviceTypes.wifi}
                    onChange={(e) => setDeviceTypes(prev => ({ ...prev, wifi: e.target.checked }))}
                    className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                  />
                  <Wifi className="w-5 h-5 text-green-500" />
                  <span className="text-foreground font-medium">WiFi Networks</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={deviceTypes.bluetooth}
                    onChange={(e) => setDeviceTypes(prev => ({ ...prev, bluetooth: e.target.checked }))}
                    className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                  />
                  <Bluetooth className="w-5 h-5 text-blue-500" />
                  <span className="text-foreground font-medium">Bluetooth Devices</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={deviceTypes.cell}
                    onChange={(e) => setDeviceTypes(prev => ({ ...prev, cell: e.target.checked }))}
                    className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
                  />
                  <Radio className="w-5 h-5 text-purple-500" />
                  <span className="text-foreground font-medium">Cell Towers</span>
                </label>
              </div>
            </div>

            {/* Search Location */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Search Location
              </h3>
              
              {/* Quick Locations */}
              <div className="mb-4">
                <label className="block text-sm text-muted-foreground mb-2">Quick Locations</label>
                <div className="grid grid-cols-2 gap-2">
                  {quickLocations.map((loc) => (
                    <button
                      key={loc.name}
                      onClick={() => handleQuickLocation(loc)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        searchLocation.quickLocation === loc.name
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border hover:bg-muted'
                      }`}
                    >
                      {loc.name.split(',')[0]}
                    </button>
                  ))}
                </div>
              </div>

              {/* City/Country Search */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">City Name</label>
                  <input
                    type="text"
                    value={searchLocation.city}
                    onChange={(e) => setSearchLocation(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="e.g., Bangalore"
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Country Code</label>
                  <input
                    type="text"
                    value={searchLocation.country}
                    onChange={(e) => setSearchLocation(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="e.g., IN, US, GB"
                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filters
                </h3>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {showFilters ? <X className="w-5 h-5" /> : <Filter className="w-5 h-5" />}
                </button>
              </div>
              
              {showFilters && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Network Name (SSID)</label>
                    <input
                      type="text"
                      value={filters.ssid}
                      onChange={(e) => setFilters(prev => ({ ...prev, ssid: e.target.value }))}
                      placeholder="Search by name"
                      className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Security Type</label>
                    <select
                      value={filters.encryption}
                      onChange={(e) => setFilters(prev => ({ ...prev, encryption: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">All Security Types</option>
                      <option value="None">Open (No Password)</option>
                      <option value="WEP">WEP (Weak)</option>
                      <option value="WPA">WPA</option>
                      <option value="WPA2">WPA2 (Recommended)</option>
                      <option value="WPA3">WPA3 (Latest)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.freeOnly}
                        onChange={(e) => setFilters(prev => ({ ...prev, freeOnly: e.target.checked }))}
                        className="rounded border-border"
                      />
                      <span className="text-sm text-foreground">Free Networks Only</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.payOnly}
                        onChange={(e) => setFilters(prev => ({ ...prev, payOnly: e.target.checked }))}
                        className="rounded border-border"
                      />
                      <span className="text-sm text-foreground">Paid Networks Only</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Search Button */}
            <button
              onClick={fetchDevices}
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground text-primary-foreground px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Search Devices
                </>
              )}
            </button>

            {/* LEA Tools */}
            {deviceTypes.cell && (
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Investigation Tools
                  </h3>
                  <button
                    onClick={() => setShowLEATools(!showLEATools)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {showLEATools ? <X className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
                  </button>
                </div>
                
                {showLEATools && (
                  <div className="space-y-4">
                    {/* Cell ID Lookup */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-foreground">Cell Tower Lookup</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          value={cellLookup.mcc}
                          onChange={(e) => setCellLookup(prev => ({ ...prev, mcc: e.target.value }))}
                          placeholder="MCC"
                          className="px-2 py-1 text-sm bg-background border border-border rounded text-foreground"
                        />
                        <input
                          type="number"
                          value={cellLookup.mnc}
                          onChange={(e) => setCellLookup(prev => ({ ...prev, mnc: e.target.value }))}
                          placeholder="MNC"
                          className="px-2 py-1 text-sm bg-background border border-border rounded text-foreground"
                        />
                        <input
                          type="number"
                          value={cellLookup.lac}
                          onChange={(e) => setCellLookup(prev => ({ ...prev, lac: e.target.value }))}
                          placeholder="LAC"
                          className="px-2 py-1 text-sm bg-background border border-border rounded text-foreground"
                        />
                        <input
                          type="number"
                          value={cellLookup.cellid}
                          onChange={(e) => setCellLookup(prev => ({ ...prev, cellid: e.target.value }))}
                          placeholder="Cell ID"
                          className="px-2 py-1 text-sm bg-background border border-border rounded text-foreground"
                        />
                      </div>
                      <button
                        onClick={handleCellLookup}
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground px-3 py-2 rounded text-sm font-medium"
                      >
                        Lookup Location
                      </button>
                      {cellLookupResult && (
                        <div className="p-2 bg-muted rounded text-xs">
                          <div>Location: {(cellLookupResult as any).lat?.toFixed(4)}, {(cellLookupResult as any).lon?.toFixed(4)}</div>
                        </div>
                      )}
                    </div>

                    {/* Area Analysis */}
                    <div className="space-y-2">
                      <button
                        onClick={handleAreaAnalysis}
                        className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground px-3 py-2 rounded text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        Analyze Area
                      </button>
                      {areaAnalysisResult && (
                        <div className="p-2 bg-muted rounded text-xs">
                          <div className="font-semibold">Cell Towers: {areaAnalysisResult.count || 0}</div>
                        </div>
                      )}
                    </div>

                    {/* Export */}
                    <button
                      onClick={handleExport}
                      className="w-full bg-accent hover:bg-accent/90 text-accent-foreground px-3 py-2 rounded text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export Data
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>

          {/* Main Map Area */}
          <div className="lg:col-span-3">
            <div className="bg-card border border-border rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: '100%', width: '100%' }}
                key={`${mapCenter[0]}-${mapCenter[1]}`}
              >
                <MapUpdater center={mapCenter} zoom={mapZoom} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | Satellite: &copy; <a href="https://www.maxar.com/">Maxar</a>'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
                {devices.map((device, idx) => (
                  <Marker
                    key={`${device.device_type}-${device.netid}-${idx}`}
                    position={[device.latitude, device.longitude]}
                    icon={getDeviceIcon(device)}
                  >
                    <Popup>
                      <div className="text-sm space-y-1">
                        <div className="font-semibold text-foreground">
                          {device.device_type === 'wifi' ? device.ssid : device.name || `Device ${device.netid}`}
                        </div>
                        {device.device_type === 'wifi' && (
                          <>
                            <div className="text-muted-foreground">Security: {device.encryption || 'Unknown'}</div>
                            {device.channel && <div className="text-muted-foreground">Channel: {device.channel}</div>}
                          </>
                        )}
                        {device.device_type === 'cell' && (
                          <>
                            <div className="text-muted-foreground">MCC: {device.mcc}, MNC: {device.mnc}</div>
                            {device.radio && <div className="text-muted-foreground">Type: {device.radio}</div>}
                            {device.averageSignalStrength && (
                              <div className="text-muted-foreground">Signal: {device.averageSignalStrength} dBm</div>
                            )}
                          </>
                        )}
                        <div className="text-muted-foreground">Location: {device.city || device.region || device.country}</div>
                        <div className="text-xs text-muted-foreground">Last seen: {new Date(device.lasttime).toLocaleDateString()}</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WiFiIntelligence;
